import type { PlatformModel } from "@/api/orval/model";
import type { AgelessAssessmentFlowType } from "@/api/orval/model";

import { platformDomainSlugSchema } from "./agelessMarketing.schemas";
import type { FlowAccessData } from "./useAgelessFlowAccess";

const PRODUCTION_AGELESS_BASE_URL = "https://ageless.ai";
export const AGELESS_SHORT_LINK_BASE_URL = "https://aglss.ai";
const SNAP_ADMIN_PATTERN = /^admin\.(\d+)\.snap\.(?:repeatmd\.app|agelessai\.dev)$/i;
const AGELESS_DEV_ADMIN_PATTERN = /^admin\.agelessai\.dev$/i;
const LOCAL_ADMIN_HOSTNAMES = new Set(["localhost", "127.0.0.1", "0.0.0.0"]);
const LOCAL_AGELESS_BASE_URL = "http://localhost:3000";

export function resolveAgelessEmbedBaseUrl(adminHostname?: string): string {
  const hostname = adminHostname ?? globalThis.location?.hostname ?? "";

  const snapMatch = SNAP_ADMIN_PATTERN.exec(hostname);
  if (snapMatch) {
    return `https://${snapMatch[1]}.snap.ageless.ai`;
  }

  if (AGELESS_DEV_ADMIN_PATTERN.test(hostname)) {
    return "https://www.agelessai.dev";
  }

  if (LOCAL_ADMIN_HOSTNAMES.has(hostname.toLowerCase())) {
    return LOCAL_AGELESS_BASE_URL;
  }

  return "https://www.ageless.ai";
}

// Default attribution baked into every generated embed snippet so leads that
// enter through a practice's website embed are distinguishable from direct
// links and the trackable-link wizard (whose "Website" option emits the generic
// utm_source=website). A distinct `website_embed` source keeps the embed channel
// from merging with that generic source in reports. These survive the embed mint
// redirect and land on assessment metadata; see AgelessAI/src/features/embedding.
export const EMBED_DEFAULT_UTM_SOURCE = "website_embed";
export const EMBED_DEFAULT_UTM_MEDIUM = "embed";

export type AgelessMarketingExperienceKey = "aesthetics" | "wellness" | "full" | "lashBrow";

export type AgelessEmbedExperienceLink = {
  readonly key: AgelessMarketingExperienceKey;
  readonly title: string;
  readonly embedUrl: string | null;
};

export type AgelessMarketingExperienceLink = {
  readonly key: AgelessMarketingExperienceKey;
  readonly title: string;
  readonly description: string;
  /**
   * The Ageless assessment flow the link opens, in the backend's terms. Stated on the link so a Meta ad launched
   * against it records its destination explicitly; null for a destination that is not an assessment flow.
   */
  readonly destinationFlowType: AgelessAssessmentFlowType | null;
  readonly url: string | null;
  readonly displayUrl: string;
  readonly shortUrl: string | null;
  readonly shortDisplayUrl: string;
  readonly isVisible: boolean;
  readonly capabilityWarning?: string | null;
};

export type AgelessMarketingUtmParameters = {
  readonly source: string;
  readonly medium: string;
  readonly campaign: string;
  readonly content: string;
  readonly term: string;
};

export function resolvePlatformDomainSlug(platform: PlatformModel | undefined): string | null {
  const domain = platform?.platformDomains?.[0]?.domain ?? "";
  const slug = domain.split(".")[0] ?? "";
  const parsed = platformDomainSlugSchema.safeParse(slug);
  return parsed.success ? parsed.data : null;
}

export type AgelessFlowAccessByType = {
  readonly aesthetics: FlowAccessData | undefined;
  readonly wellness: FlowAccessData | undefined;
  readonly combined: FlowAccessData | undefined;
  readonly lashBrow: FlowAccessData | undefined;
};

type AgelessEmbedBaseUrlOptions = {
  readonly agelessBaseUrl?: string;
  readonly adminHostname?: string;
};

function normalizePlatformId(platformId: string | undefined): string | null {
  const normalized = platformId?.trim().toLowerCase();
  if (!normalized) {
    return null;
  }

  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(normalized)
    ? normalized
    : null;
}

function buildEmbedFlowUrl(
  baseUrl: string,
  normalizedPlatformId: string | null,
  platformDomainSlug: string | null,
  flow: "transformation" | "try-on" | "wellness" | "lash-brow",
): string | null {
  if (!normalizedPlatformId || !platformDomainSlug) {
    return null;
  }

  const flowUrl = `${baseUrl}/embed/platforms/${normalizedPlatformId}/a/${platformDomainSlug}/${flow}`;
  return appendDefaultEmbedUtmParameters(flowUrl);
}

export function appendDefaultEmbedUtmParameters(url: string): string {
  const trackedUrl = new URL(url);
  trackedUrl.searchParams.set("utm_source", EMBED_DEFAULT_UTM_SOURCE);
  trackedUrl.searchParams.set("utm_medium", EMBED_DEFAULT_UTM_MEDIUM);
  return trackedUrl.toString();
}

export function buildAgelessEmbedExperienceLinks(
  platformId: string | undefined,
  platformDomainSlug: string | null,
  flowAccess?: AgelessFlowAccessByType,
  options?: AgelessEmbedBaseUrlOptions,
): readonly AgelessEmbedExperienceLink[] {
  const hasAesthetic = flowAccess ? flowAccess.aesthetics?.isFlowActive === true : true;
  const hasWellness = flowAccess ? flowAccess.wellness?.isFlowActive === true : true;
  const hasLashBrow = flowAccess ? flowAccess.lashBrow?.isFlowActive === true : true;
  const configuredBaseUrl = options?.agelessBaseUrl?.trim();
  const baseUrl = configuredBaseUrl
    ? configuredBaseUrl.replace(/\/$/, "")
    : resolveAgelessEmbedBaseUrl(options?.adminHostname);
  const normalizedPlatformId = normalizePlatformId(platformId);
  const links: AgelessEmbedExperienceLink[] = [];

  if (hasAesthetic && hasWellness) {
    links.push({
      key: "full",
      title: "Aesthetics + Wellness Experience",
      embedUrl: buildEmbedFlowUrl(
        baseUrl,
        normalizedPlatformId,
        platformDomainSlug,
        "transformation",
      ),
    });
  }

  if (hasAesthetic) {
    links.push({
      key: "aesthetics",
      title: "Aesthetics Experience",
      embedUrl: buildEmbedFlowUrl(baseUrl, normalizedPlatformId, platformDomainSlug, "try-on"),
    });
  }

  if (hasWellness) {
    links.push({
      key: "wellness",
      title: "Wellness Experience",
      embedUrl: buildEmbedFlowUrl(baseUrl, normalizedPlatformId, platformDomainSlug, "wellness"),
    });
  }

  if (hasLashBrow) {
    links.push({
      key: "lashBrow",
      title: "Lash & Brow Experience",
      embedUrl: buildEmbedFlowUrl(baseUrl, normalizedPlatformId, platformDomainSlug, "lash-brow"),
    });
  }

  return links;
}

export function buildAgelessMarketingExperienceLinks(
  platformDomainSlug: string | null,
  agelessBaseUrl = PRODUCTION_AGELESS_BASE_URL,
  flowAccess?: AgelessFlowAccessByType,
): readonly AgelessMarketingExperienceLink[] {
  const agelessExperienceBaseUrl = `${agelessBaseUrl.replace(/\/$/, "")}/a`;
  const hasAesthetic = flowAccess ? flowAccess.aesthetics?.isFlowActive === true : true;
  const hasWellness = flowAccess ? flowAccess.wellness?.isFlowActive === true : true;
  const hasLashBrow = flowAccess ? flowAccess.lashBrow?.isFlowActive === true : true;
  const hasAestheticCapabilities = flowAccess?.aesthetics?.hasCapabilities;
  const hasWellnessCapabilities = flowAccess?.wellness?.hasCapabilities;
  const hasLashBrowCapabilities = flowAccess?.lashBrow?.hasCapabilities;

  const aestheticsWellnessExperienceUrl = platformDomainSlug
    ? `${agelessExperienceBaseUrl}/${platformDomainSlug}/transformation`
    : null;
  const aestheticsAndWellnessExperienceShortUrl = platformDomainSlug
    ? `${AGELESS_SHORT_LINK_BASE_URL}/${platformDomainSlug}`
    : null;
  const aestheticsExperienceUrl = platformDomainSlug
    ? `${agelessExperienceBaseUrl}/${platformDomainSlug}/try-on`
    : null;
  const wellnessExperienceUrl = platformDomainSlug
    ? `${agelessExperienceBaseUrl}/${platformDomainSlug}/wellness`
    : null;
  const lashBrowExperienceUrl = platformDomainSlug
    ? `${agelessExperienceBaseUrl}/${platformDomainSlug}/lash-brow`
    : null;
  const aestheticsExperienceShortUrl = platformDomainSlug
    ? `${AGELESS_SHORT_LINK_BASE_URL}/a/${platformDomainSlug}`
    : null;
  const wellnessExperienceShortUrl = platformDomainSlug
    ? `${AGELESS_SHORT_LINK_BASE_URL}/w/${platformDomainSlug}`
    : null;
  const lashBrowExperienceShortUrl = platformDomainSlug
    ? `${AGELESS_SHORT_LINK_BASE_URL}/l/${platformDomainSlug}`
    : null;

  return [
    {
      key: "full",
      destinationFlowType: "AestheticsWellness",
      title: "Aesthetics + Wellness Experience",
      description:
        "For practices that offer both aesthetics and wellness. Patients choose their path.",
      url: aestheticsWellnessExperienceUrl,
      displayUrl: toDisplayUrl(aestheticsWellnessExperienceUrl),
      shortUrl: aestheticsAndWellnessExperienceShortUrl,
      shortDisplayUrl: toDisplayUrl(aestheticsAndWellnessExperienceShortUrl),
      isVisible: hasAesthetic && hasWellness,
      capabilityWarning: resolveCapabilityWarning(
        "full",
        hasAestheticCapabilities,
        hasWellnessCapabilities,
        hasLashBrowCapabilities,
      ),
    },
    {
      key: "aesthetics",
      destinationFlowType: "Aesthetics",
      title: "Aesthetics Experience",
      description:
        "For patients interested in injectables, skin rejuvenation, body contouring, and facial procedures",
      url: aestheticsExperienceUrl,
      displayUrl: toDisplayUrl(aestheticsExperienceUrl),
      shortUrl: aestheticsExperienceShortUrl,
      shortDisplayUrl: toDisplayUrl(aestheticsExperienceShortUrl),
      isVisible: hasAesthetic,
      capabilityWarning: resolveCapabilityWarning(
        "aesthetics",
        hasAestheticCapabilities,
        hasWellnessCapabilities,
        hasLashBrowCapabilities,
      ),
    },
    {
      key: "wellness",
      destinationFlowType: "Wellness",
      title: "Wellness Experience",
      description:
        "For patients interested in weight loss, hormone therapy, peptides, IV therapy, and longevity treatments",
      url: wellnessExperienceUrl,
      displayUrl: toDisplayUrl(wellnessExperienceUrl),
      shortUrl: wellnessExperienceShortUrl,
      shortDisplayUrl: toDisplayUrl(wellnessExperienceShortUrl),
      isVisible: hasWellness,
      capabilityWarning: resolveCapabilityWarning(
        "wellness",
        hasAestheticCapabilities,
        hasWellnessCapabilities,
        hasLashBrowCapabilities,
      ),
    },
    {
      key: "lashBrow",
      destinationFlowType: "LashBrow",
      title: "Lash & Brow Experience",
      description:
        "For patients interested in eyelash extensions, lash lifts, brow lamination, microblading, and lash & brow tinting",
      url: lashBrowExperienceUrl,
      displayUrl: toDisplayUrl(lashBrowExperienceUrl),
      shortUrl: lashBrowExperienceShortUrl,
      shortDisplayUrl: toDisplayUrl(lashBrowExperienceShortUrl),
      isVisible: hasLashBrow,
      capabilityWarning: resolveCapabilityWarning(
        "lashBrow",
        hasAestheticCapabilities,
        hasWellnessCapabilities,
        hasLashBrowCapabilities,
      ),
    },
  ];
}

function resolveCapabilityWarning(
  key: AgelessMarketingExperienceKey,
  hasAestheticCapabilities: boolean | undefined,
  hasWellnessCapabilities: boolean | undefined,
  hasLashBrowCapabilities: boolean | undefined,
): string | null {
  // Lash & Brow is orthogonal to the aesthetics/wellness pair, so it has its
  // own undefined-guard rather than sharing the two-flow guard below.
  if (key === "lashBrow") {
    if (hasLashBrowCapabilities === undefined) {
      return null;
    }
    return hasLashBrowCapabilities
      ? null
      : "This flow is not accessible to patients yet. Add lash & brow treatments in the services catalog to activate it.";
  }

  if (hasAestheticCapabilities === undefined || hasWellnessCapabilities === undefined) {
    return null;
  }

  switch (key) {
    case "aesthetics":
      return hasAestheticCapabilities
        ? null
        : "This flow is not accessible to patients yet. Add aesthetic treatments in the services catalog to activate it.";
    case "wellness":
      return hasWellnessCapabilities
        ? null
        : "This flow is not accessible to patients yet. Add wellness treatments in the services catalog to activate it.";
    case "full":
      if (!hasAestheticCapabilities || !hasWellnessCapabilities) {
        return "This flow is not accessible to patients yet. Please ensure you have aesthetic and wellness treatments selected in the services catalog to activate it.";
      }

      return null;
    default:
      return null;
  }
}

export function buildTrackedMarketingUrl(
  baseUrl: string | null,
  utmParameters: AgelessMarketingUtmParameters,
): string | null {
  if (!baseUrl) {
    return null;
  }

  const trackedUrl = new URL(baseUrl);
  const normalizedParameters: ReadonlyArray<readonly [string, string]> = [
    ["utm_source", utmParameters.source],
    ["utm_medium", utmParameters.medium],
    ["utm_campaign", utmParameters.campaign],
    ["utm_content", utmParameters.content],
    ["utm_term", utmParameters.term],
  ];

  normalizedParameters.forEach(([key, value]) => {
    const normalizedValue = value.trim();
    if (!normalizedValue) {
      return;
    }
    trackedUrl.searchParams.set(key, normalizedValue);
  });

  return trackedUrl.toString();
}

export function toDisplayUrl(url: string | null): string {
  if (!url) {
    return "";
  }

  return url.replace(/^https?:\/\//, "");
}

export type AgelessShortLinkOption = {
  readonly key: AgelessMarketingExperienceKey;
  readonly label: string;
  readonly shortUrl: string;
};

export type AgelessMessagingFlowOption = {
  readonly key: AgelessMarketingExperienceKey;
  readonly value: AgelessAssessmentFlowType;
  readonly label: string;
};

const MESSAGING_FLOW_OPTIONS: ReadonlyArray<AgelessMessagingFlowOption> = [
  { key: "aesthetics", value: "Aesthetics", label: "Aesthetic" },
  { key: "wellness", value: "Wellness", label: "Wellness" },
  { key: "full", value: "AestheticsWellness", label: "Combo" },
  { key: "lashBrow", value: "LashBrow", label: "Lash & Brow" },
];

export function buildAvailableAgelessMessagingFlowOptions(
  flowAccess?: AgelessFlowAccessByType,
): readonly AgelessMessagingFlowOption[] {
  return MESSAGING_FLOW_OPTIONS.filter(({ key }) => isFlowAvailable(flowAccess, key));
}

function isFlowAvailable(
  flowAccess: AgelessFlowAccessByType | undefined,
  key: AgelessMarketingExperienceKey,
): boolean {
  const flow = getFlowAccessByKey(flowAccess, key);
  return flow?.isFlowActive === true;
}

function getFlowAccessByKey(
  flowAccess: AgelessFlowAccessByType | undefined,
  key: AgelessMarketingExperienceKey,
): FlowAccessData | undefined {
  switch (key) {
    case "aesthetics":
      return flowAccess?.aesthetics;
    case "wellness":
      return flowAccess?.wellness;
    case "full":
      return flowAccess?.combined;
    case "lashBrow":
      return flowAccess?.lashBrow;
    default:
      return undefined;
  }
}
